import { asStr } from '@paytrace/pt-skel/Helper'
import { toNumber } from 'lodash'
import React, { CSSProperties } from 'react'
import { ActionMenu, ActionMenuItem, TriggerType } from '../Component/EnhancedData/ActionMenu'
import RowInputText from '../Component/EnhancedData/RowInputText'
import { RowDataWithKey } from '../Component/EnhancedData/SortableTable/Types'
import {
  DataWithKey,
  RowErrorHandler,
  RowFieldDefinition,
  RowInputChangeHandler,
} from '../Component/EnhancedData/Types'
import { ThreeDotIcon } from '../Component/Icons'
import { AlertIcon } from '../Component/Icons/AlertIcon'
import { ED_FIELD_SPEC } from '../Helper/EnhancedData/Constants'
import { formatter } from '../Helper/Money'
import { toCSSClassNameWithSuffix, toMapByKey } from '../Helper/util'
import {
  ProductDropdownProps,
} from '../Page/EnhancedData/ManageEnhancedData/Level3Segments/LineItemTable/ProductDropdown'
import * as EDT from '../Type/EnhancedData'
import { LineItemRowError, LineItemRowErrorMap } from '../Type/EnhancedData'
import { SelfKVPair } from '../types'

const FIELD_CONFIGS: Partial<Record<keyof EDT.LineItemStateData, {
  width?: string;
  isSpecialReturn?: (val: string) => string;
}>> = {
  rowNumber: {
    isSpecialReturn: (val) => val,
  },
  productName: {
    width: '300px',
  },
  quantity: {
    width: '80px',
  },
  unitOfMeasure: {
    width: '80px',
  },
  costPerUnit: {
    width: '80px',
  },
  itemTax: {
    width: '80px',
  },
  amount: {
    isSpecialReturn: (val) => formatter.format(toNumber(val)),
  },
}

const buildRowInputField = (
  fieldName: keyof EDT.LineItemStateData,
  columnDef: EDT.LineItemColumDefinition,
  rowKey: string | number,
  rowValue: string,
  onRowInputChange: RowInputChangeHandler<EDT.LineItemStateData>,
  onRowError?: RowErrorHandler,
  onRowInputFocus?: (key: string | number, field: string, value: string) => void
): React.ReactNode => {
  const fieldConfig = FIELD_CONFIGS[fieldName]
  if (fieldConfig?.isSpecialReturn) {
    return fieldConfig.isSpecialReturn(rowValue)
  }

  let renderedSuffixIcon = columnDef?.dataSuffixIcon || ''
  if (fieldName === 'productName' && React.isValidElement(renderedSuffixIcon)) {
    renderedSuffixIcon = React.cloneElement(
      renderedSuffixIcon as React.ReactElement<ProductDropdownProps>,
      { rowKey }
    )
  }

  const fieldSpec = ED_FIELD_SPEC.line_item[fieldName as keyof typeof ED_FIELD_SPEC.line_item]
  const fieldDefinition: RowFieldDefinition = {
    name: String(fieldName),
    maxLength: fieldSpec?.maxLength ?? 50,
    minLength: fieldSpec?.minLength ?? 0,
    pattern: fieldSpec?.pattern ?? '',
    errorMessage: fieldSpec?.errorMessage ?? '',
    dataType: columnDef?.dataType,
  }

  // TODO: remove placeholder from column definition
  const placeholder = (columnDef?.placeholder || fieldSpec?.placeholder) ?? ''

  const customStyle: CSSProperties = fieldConfig?.width ? { width: fieldConfig.width } : {}

  return (
    <RowInputText
      key={`${rowKey}-${fieldName}`}
      rowKey={rowKey}
      value={rowValue}
      placeholder={placeholder}
      fieldDefinition={fieldDefinition}
      onInputChange={onRowInputChange}
      onRowInputFocus={onRowInputFocus}
      style={customStyle}
      className={toCSSClassNameWithSuffix(String(fieldName), 'input')}
      prefixIcon={columnDef?.dataPrefixIcon || ''}
      suffixIcon={renderedSuffixIcon}
      onRowError={onRowError}
    />
  )
}

const toRowInputs = (
  rowKey: string | number,
  lineItem: DataWithKey<EDT.LineItemStateData>,
  onRowInputChange: RowInputChangeHandler<EDT.LineItemStateData>,
  columnDefMap: SelfKVPair<EDT.LineItemColumDefinition>,
  onRowError?: RowErrorHandler,
  onRowFocus?: (key: string | number, field: string, value: string) => void
): RowDataWithKey<EDT.LineItemRow> => {
  const inputFieldsEntries = Object.entries(columnDefMap).map(([key, colDef]) => {
    const fieldName = key as keyof EDT.LineItemStateData
    const rowValue = lineItem[fieldName]
    const inputElement = buildRowInputField(
      fieldName,
      colDef,
      rowKey,
      asStr(rowValue),
      onRowInputChange,
      onRowError,
      onRowFocus
    )
    return [fieldName, inputElement]
  })

  return {
    key: rowKey,
    ...Object.fromEntries(inputFieldsEntries),
  }
}

export const toLineItemRows = (
  mappedLineItem: Record<string, DataWithKey<EDT.LineItemStateData>>,
  columnDefs: EDT.LineItemColumDefinition[],
  onRowChange: RowInputChangeHandler<EDT.LineItemStateData>,
  onRowError?: RowErrorHandler,
  onRowFocus?: (key: string | number, field: string, value: string) => void
): RowDataWithKey<EDT.LineItemRow>[] => {
  const columnDefMap = toMapByKey<EDT.LineItemColumDefinition>(columnDefs, 'id')
  return Object.entries(mappedLineItem).map(
    ([rowKey, rowData]) => toRowInputs(rowKey, rowData, onRowChange, columnDefMap, onRowError, onRowFocus)
  )
}

export const toMappedActionMenu = (
  mappedLineItem: Record<string, DataWithKey<EDT.LineItemStateData>>,
  rowActions: ActionMenuItem[],
  triggerMode: TriggerType = 'hover',
  triggerIcon: React.JSX.Element = <ThreeDotIcon/>
): Record<string, React.JSX.Element> => {
  const actionMenuEntries = Object.entries(mappedLineItem).map(
    ([rowKey, rowData]) => {
      const menuComponent = (
        <ActionMenu
          actions={rowActions}
          actionParam={rowData}
          triggerMode={triggerMode}
          triggerIcon={triggerIcon}
          key={rowKey}
        />
      )
      return [rowKey, menuComponent] as const
    }
  )
  return Object.fromEntries(actionMenuEntries)
}

export const updateLineItemErrorMap = (
  prevRowErrorMaps: LineItemRowErrorMap,
  rowKey: string | number,
  fieldName: string,
  errorMessage: string | null,
  rowIndex: number,
  rowNumber: number,
  isErrorVisible: boolean,
): LineItemRowErrorMap => {
  const initData: LineItemRowError = { index: rowIndex, fieldErrorMap: {}, rowNumber }
  const currentRowError: LineItemRowError = { ...prevRowErrorMaps[rowKey] ?? initData }
  const currentFieldErrorMap = currentRowError.fieldErrorMap

  // phase 1: update field error map
  if (!errorMessage) {
    delete currentFieldErrorMap[fieldName]
  } else {
    currentFieldErrorMap[fieldName] = {
      message: errorMessage,
      isVisible: isErrorVisible,
    }
  }

  // phase 2: update row error map
  const currentRowHasErrors = Object.keys(currentFieldErrorMap).length > 0
  const nextRowErrorMaps = { ...prevRowErrorMaps }
  if (!currentRowHasErrors) {
    delete nextRowErrorMaps[rowKey]
  } else {
    nextRowErrorMaps[rowKey] = {
      index: rowIndex,
      rowNumber,
      fieldErrorMap: currentFieldErrorMap,
    }
  }
  return nextRowErrorMaps
}

export const toRowErrorElements = (
  lineItemRowErrorMaps: LineItemRowErrorMap
): Record<string, React.JSX.Element> => {
  const map: Record<string, React.JSX.Element> = {}

  if (!lineItemRowErrorMaps) return map

  for (const [rowKey, lineItemRowError] of Object.entries(lineItemRowErrorMaps)) {
    const fieldErrorMap = lineItemRowError?.fieldErrorMap
    if (!fieldErrorMap) continue

    const activeErrors = Object.entries(fieldErrorMap).filter(
      ([_, fieldError]) => Boolean(fieldError.message) && fieldError.isVisible
    )

    if (activeErrors.length > 0) {
      map[rowKey] = (
        <div
          role="alert"
          style={{
            color: '#E16642',
            fontSize: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {activeErrors.map(([field, fieldError]) => (
            <div
              key={field}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <AlertIcon />
              <span>{fieldError.message}</span>
            </div>
          ))}
        </div>
      )
    }
  }

  return map
}
